import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminShell from './AdminShell';
import { api } from '../../lib/api';
import { Plus, Trash2, ArrowRight, CheckCircle2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { STEPS } from './workflow/shared';

const AdminWorkflows = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/workflows');
      setItems(res.data);
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm('Delete this workflow? The published post (if any) is kept.')) return;
    try {
      await api.delete(`/admin/workflows/${id}`);
      toast({ title: 'Workflow deleted' });
      load();
    } catch (e) {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  return (
    <AdminShell title="AI content workflow">
      <div className="flex justify-between items-center mb-4">
        <div className="text-xs text-ink-3">
          Keyword research → AI draft → human review → SEO → publish. Progress is saved after every step so the team can pick up where they left off.
        </div>
        <Link to="/admin/workflows/new" className="btn-primary" data-testid="workflow-new-btn">
          <Plus className="w-4 h-4" /> New AI workflow
        </Link>
      </div>
      <div className="bg-white rounded-2xl border border-[var(--line-2)] overflow-hidden" data-testid="workflow-list">
        {loading ? (
          <div className="p-8 text-center text-ink-3 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-ink-3 text-sm">No workflows yet. Start one to research keywords and draft a post with AI.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-alt)] text-ink-3 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-3">Topic / Title</th>
                <th className="text-left px-4 py-3">Progress</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Updated</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((w) => {
                const done = w.completed_steps?.length || 0;
                const step = STEPS[Math.min(w.current_step, STEPS.length) - 1];
                return (
                  <tr key={w.id} className="border-t border-[var(--line-2)]" data-testid={`workflow-row-${w.id}`}>
                    <td className="px-4 py-3 text-ink font-medium">
                      {w.article?.title || w.topic || 'Untitled'}
                      {w.article?.title && w.topic && <div className="text-[11px] text-ink-3 font-normal">{w.topic}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-1.5 rounded-full bg-[var(--bg-alt)] overflow-hidden">
                          <div className="h-full bg-[var(--ink)]" style={{ width: `${(done / STEPS.length) * 100}%` }} />
                        </div>
                        <span className="text-[11px] text-ink-3">Step {w.current_step} · {step?.title}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${
                        w.status === 'published' ? 'bg-emerald-50 text-emerald-700' : w.status === 'drafted' ? 'bg-amber-50 text-amber-700' : 'bg-[var(--bg-alt)] text-ink-3'
                      }`}>
                        {w.status === 'published' && <CheckCircle2 className="w-3 h-3" />}
                        {w.status === 'published' ? 'Published' : w.status === 'drafted' ? 'Draft post created' : 'In progress'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-ink-3">{new Date(w.updated_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-3">
                        <Link to={`/admin/workflows/${w.id}`} className="text-ink hover:text-ink-2 inline-flex items-center gap-1 text-xs" data-testid={`workflow-continue-${w.id}`}>
                          Continue <ArrowRight className="w-3 h-3" />
                        </Link>
                        <button onClick={() => remove(w.id)} className="text-ink-2 hover:text-red-600" title="Delete" data-testid={`workflow-delete-${w.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </AdminShell>
  );
};

export default AdminWorkflows;
